/**
 * Cart Preservation System for Buy Now Functionality
 * Saves current cart before direct checkout, restores when user returns
 */
(function() {
  'use strict';

  // Namespace to avoid conflicts
  const CartPreservation = {
    STORAGE_KEY: 'shopify_preserved_cart',
    
    // Initialize the system
    init: function() {
      this.restoreCartIfNeeded();
      this.bindEvents();
    },

    // Save current cart to localStorage
    saveCart: async function() {
      try {
        const response = await fetch('/cart.js', {
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const cart = await response.json();
        
        if (!cart.items || !Array.isArray(cart.items)) {
          console.warn('[CartPreservation] Invalid cart data received');
          return false;
        }

        const cartData = {
          items: cart.items.map(item => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
          })),
          note: cart.note || '',
          attributes: cart.attributes || {},
          timestamp: Date.now()
        };

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cartData));
        return true;
        
      } catch (error) {
        console.error('[CartPreservation] Failed to save cart:', error);
        return false;
      }
    },

    // Execute buy now flow with cart preservation
    buyNow: async function(variantId) {
      if (!variantId) {
        console.error('[CartPreservation] No variant ID provided');
        return;
      }

      const saved = await this.saveCart();
      
      if (saved) {
        window.location.href = `/cart/${variantId}:1`;
      } else {
        console.error('[CartPreservation] Could not save cart, aborting buy-now flow');
        // Fallback to regular add to cart
        this.fallbackAddToCart(variantId);
      }
    },

    // Restore preserved cart if exists
    restoreCartIfNeeded: async function() {
      const savedData = localStorage.getItem(this.STORAGE_KEY);
      if (!savedData) return;

      try {
        const cartData = JSON.parse(savedData);
        
        // Check if data is too old (24 hours)
        const isExpired = Date.now() - cartData.timestamp > 24 * 60 * 60 * 1000;
        if (isExpired) {
          localStorage.removeItem(this.STORAGE_KEY);
          return;
        }

        // Check current cart state
        const currentCartResponse = await fetch('/cart.js', {
          credentials: 'same-origin'
        });
        
        if (!currentCartResponse.ok) {
          throw new Error('Failed to fetch current cart');
        }
        
        const currentCart = await currentCartResponse.json();
        
        // Only restore if current cart is empty
        // if (currentCart.item_count > 0) {
        //   localStorage.removeItem(this.STORAGE_KEY);
        //   return;
        // }

        await this.restoreCartItems(cartData);
        localStorage.removeItem(this.STORAGE_KEY);
        
        // Trigger cart update event for theme components
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
      } catch (error) {
        console.error('[CartPreservation] Failed to restore cart:', error);
        localStorage.removeItem(this.STORAGE_KEY);
      }
    },

    // Restore cart items and attributes
    restoreCartItems: async function(cartData) {
      // Restore items
      if (cartData.items && cartData.items.length > 0) {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: cartData.items })
        });

        if (!addResponse.ok) {
          throw new Error('Failed to add items to cart');
        }
      }

      // Restore cart note and attributes
      if (cartData.note || Object.keys(cartData.attributes).length > 0) {
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            note: cartData.note,
            attributes: cartData.attributes
          })
        });

        if (!updateResponse.ok) {
          console.warn('[CartPreservation] Failed to update cart attributes');
        }
      }
      this.refreshCartPageIfNeeded();
    },

     refreshCartPageIfNeeded: function() {
      // Only refresh if we're on the cart page
      if (window.location.pathname === '/cart') {
          window.location.reload();
      }
    },

    // Fallback to regular add to cart if preservation fails
    fallbackAddToCart: async function(variantId) {
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });
        
        window.location.href = '/cart';
      } catch (error) {
        console.error('[CartPreservation] Fallback add to cart failed:', error);
      }
    },

    // Bind event listeners
    bindEvents: function() {
      // Listen for buy now button clicks
      document.addEventListener('click', (event) => {
        if (event.target.matches('[data-buy-now]')) {
          event.preventDefault();
          const variantId = event.target.dataset.variantId || event.target.dataset.buyNow;
          if (variantId) {
            this.buyNow(variantId);
          }
        }
      });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartPreservation.init());
  } else {
    CartPreservation.init();
  }

  // Expose to global scope for manual calls
  window.CartPreservation = CartPreservation;

})();
